/**
 * 修复 Markdown 加粗语法 (**) 在中文字符、数字及标点符号紧密粘连时无法解析的 Bug。
 *
 * 【核心原理】
 * 依照 CommonMark 国际规范，当 ** 的边界遇到“标点+汉字/数字”时（例如 `**79.2%**至`），
 * 解析器会判定字符未断开而拒绝将其闭合。本函数通过在 ** 外侧动态插入“零宽空格 (ZWSP, \u200B)”，
 * 强行制造字符断词边界，既能完美触发 Markdown 闭合解析，又不会在网页上产生肉眼可见的物理空格。
 *
 * 【幂等性/安全性设计】
 * 正则使用 `[^\s\u200B]` 代替标准的 `\S`。确保在 AI 文本流式传输 (Streaming) 或组件频繁
 * Re-render（重新渲染）时，绝对不会重复堆叠插入零宽空格，避免字符串体积异常暴增。
 *
 * @param {string} text - 包含 Markdown 语法的原始文本
 * @returns {string} 处理后能够稳定解析加粗的文本
 */
export const formatMarkdownSpaces = (text: string) => {
  const ZWSP = "\u200B"; // 零宽空格
  // 安全防御：如果传入的不是标准字符串（如 null/undefined），原样返回防止系统崩溃
  if (typeof text !== "string") {
    return text;
  }
  return (
    text
      /**
       * 1. 修复左侧开标签 (例如: 暴增**79.2%)
       * 匹配组1 ([^\s\u200B]): ** 左侧紧贴着一个“既不是标准空白、也不是已有 ZWSP”的有效字符
       * 匹配组2 (\*\*): 加粗符号本身
       * 替换动作: 在两者中间强行塞入一个零宽空格
       */
      .replace(/([^\s\u200B])(\*\*)/g, `$1${ZWSP}$2`)
      /**
       * 2. 修复右侧闭标签 (例如: 79.2%**至)
       * 匹配组1 (\*\*): 加粗符号本身
       * 匹配组2 ([^\s\u200B]): ** 右侧紧贴着一个“既不是标准空白、也不是已有 ZWSP”的有效字符
       * 替换动作: 在两者中间强行塞入一个零宽空格
       */
      .replace(/(\*\*)([^\s\u200B])/g, `$1${ZWSP}$2`)
  );
};

export const formatMsgQuotes = (message: string) => {
  if (!message) {
    return "";
  }
  const withoutZero = message.replace(/\[\^0\^?\]/g, "");
  const withoutInvalid = withoutZero.replace(/\[\^(?!\d+\^?\])[^\]]*\]/g, "");

  // 清理明显非法的非数字引用，并兼容 [^n^] 和 [^n] 两种格式
  return withoutInvalid.replace(/(?:\[\^\d+\^?\])+(?!\()/g, (match) => {
    const numbers =
      Array.from(match.matchAll(/\[\^(\d+)\^?\]/g), (item) => item[1]) || [];
    return `<quotes>${numbers.join(",")}</quotes>`;
  });
};
